// Transient session lifecycle state shared with AuthStore consumers (SG-WU-002 / R3.2, R3.3).
//
// It lives OUTSIDE the AuthStore state on purpose: `pinia-plugin-persistedstate` persists the whole store
// state, and a "logout in progress" flag must never be written to storage. It is also a plain module so
// consumers do not depend on the AuthStore instance (nor on a cached getter) to read it.
import { ref } from "vue";

/** `true` from the moment a logout starts until its shared operation has finished cleaning up. */
export const closingSession = ref(false);

/**
 * Direct read of whether a session is being closed. While it is `true` the JWT is still in the store (the
 * logout needs it to notify the backend), but the session is not usable: nothing new may be authenticated
 * with it, and it must not be mistaken for a legacy hydrated session.
 */
export const isSessionClosing = (): boolean => closingSession.value;

/**
 * Authentication generation (SG-AUD-21): a transient counter that advances ONLY when an authentication
 * succeeds (password, PIN or kiosk login), never when the store is hydrated. A logout remembers the value
 * it started under; if the value changed when it finishes, a genuinely newer login happened meanwhile and
 * the older logout must not clear it. Rehydrating the persisted snapshot of the ORIGINAL session (or a
 * legacy one) restores state but does not advance it, so that logout still cleans up. In memory only: it
 * is never written to storage, and it does not depend on `sessionId` or on token (in)equality.
 */
let authGeneration = 0;
export const currentAuthGeneration = (): number => authGeneration;
export const advanceAuthGeneration = (): void => {
  authGeneration++;
};
